import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import css from "./MarkdownRenderer.module.css";
import { memo, useMemo } from "react";
import { SentimentBadge } from "$root/functional-areas/market-view/analysis/SentimentBadge";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import type { DataAttributesProps } from "@mdotm/shadcn/mdotm/components";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";

export type MarkdownRendererProps = {
	children?: string;
	componentOverrides?: Partial<Components>;
	title?: string;
} & DataAttributesProps &
	Pick<StylableProps, "className">;

export const MarkdownRenderer = memo(MarkdownRendererBare) as typeof MarkdownRendererBare;

/** Extracts the concatenated text content of a hast node. */
function hastNodeToText(node: unknown): string {
	if (node == null || typeof node !== "object") {
		return "";
	}
	const { value, children } = node as { value?: unknown; children?: unknown };
	if (typeof value === "string") {
		return value;
	}
	if (Array.isArray(children)) {
		return children.map(hastNodeToText).join("");
	}
	return "";
}

/**
 * Detects whether a table cell holds a numeric value, tolerating common
 * formatting such as thousands separators, currency symbols, percentages,
 * leading sign and surrounding whitespace (e.g. "1,234.56", "-3.2%", "$1,000").
 */
function isNumericCell(text: string): boolean {
	const trimmed = text.trim();
	if (trimmed === "") {
		return false;
	}
	const withoutDecorations = trimmed.replace(/[\s,$€£%]/g, "");
	return /^[+-]?(\d+(\.\d+)?|\.\d+)$/.test(withoutDecorations);
}

/**
 * The tag wrappers react-markdown renders with. `hast-util-to-jsx-runtime` uses these values as
 * element types, so they live at module scope: React compares element types by reference, and a
 * per-render map would remount the whole rendered subtree on every markdown change.
 */
const markdownComponents: Components = {
	h1: ({ node: _node, ...props }) => (
		<h1 className="text-[16px] font-bold mb-2 pt-2" {...props}>
			{props.children}
		</h1>
	),
	h2: ({ node: _node, ...props }) => (
		<h2 className="text-[14px] font-bold mb-2 pt-2" {...props}>
			{props.children}
		</h2>
	),
	h3: ({ node: _node, ...props }) => (
		<h3 className="text-[12px] font-bold mb-2 pt-2" {...props}>
			{props.children}
		</h3>
	),
	ul: ({ node: _node, ...props }) => <ul className="list-disc pl-4" {...props} />,
	ol: ({ node: _node, ...props }) => <ol className="list-decimal pl-4" {...props} />,
	li: ({ node: _node, ...props }) => <li {...props} />,
	// A markdown paragraph can hold nested block content, which the HTML parser de-nests out of a
	// <p>; a <div> keeps that content where the markdown put it.
	p: ({ node: _node, ...props }) => <div className="text mb-2" {...props} />,
	blockquote: ({ node: _node, ...props }) => <blockquote className="bg-sky-100 px-2 py-1 rounded-sm" {...props} />,
	table: ({ node: _node, ...props }) => <table {...props} />,
	th: ({ node, ...props }) => (
		<th className={isNumericCell(hastNodeToText(node)) ? "text-right" : undefined} {...props} />
	),
	td: ({ node, ...props }) => (
		<td className={isNumericCell(hastNodeToText(node)) ? "text-right" : undefined} {...props} />
	),
	code: ({ node: _node, ...props }) => <span {...props} />,
	pre: ({ node: _node, ...props }) => <i {...props} />,
	template: ({ node: _node, ...forward }) => {
		switch ((forward as any)["data-custom-element"]) {
			case "sentiment":
				return (
					<SentimentBadge
						size="x-small"
						//we can't really guarantee type safety here, we'll just use a sprinkle of "as" to make TypeScript happy
						sentiment={((forward as any)["data-sentiment"] ?? "neutral") as "neutral"}
						indicator={((forward as any)["data-indicator"] ?? "driver") as "driver"}
					>
						{(forward as any)["data-label"] ?? ""}
					</SentimentBadge>
				);
			default:
				return <template {...forward} />;
		}
	},
};

function MarkdownRendererBare({
	children,
	componentOverrides,
	className,
	title,
	...dataAttributes
}: MarkdownRendererProps): React.JSX.Element {
	const components = useMemo<Components>(
		() => (componentOverrides ? { ...markdownComponents, ...componentOverrides } : markdownComponents),
		[componentOverrides],
	);

	return (
		<div className={overrideClassName(css.markdown, className)} title={title}>
			<ReactMarkdown
				skipHtml
				remarkPlugins={[remarkGfm]}
				rehypePlugins={[rehypeRaw]}
				components={components}
				{...dataAttributes}
			>
				{children ?? ""}
			</ReactMarkdown>
		</div>
	);
}
